import { useState } from 'react';
import { Button, Tooltip } from '@heroui/react';
import { X } from 'lucide-react';
import type {
  IAssignmentSubmissionDetails,
  IAssignmentSubmissionRequest,
  IAssignmentSubmissionStudentInfo,
} from '@/entities/Assignment';
import { AssignmentSubmissionStatusEnum } from '@/entities/Assignment';
import { TurnitinSubmissionStatus } from '@/entities/Turnitin';
import { AttachmentsDownloadList } from '@/shared/ui/AttachmentsDownloadList';
import EditIcon from '@/shared/assets/icons/edit.svg';
import { formatDateTimeWithAMPM } from '@/shared/lib/utils/formatLondonDateTime';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import { Svg } from '@/shared/ui/Svg';
import { MessageComposer } from '../../MessageComposer';

interface ISubmissionProps {
  student: IAssignmentSubmissionStudentInfo;
  submission: IAssignmentSubmissionDetails;
  userRole: 'student' | 'teacher';
  assignmentStatus?: AssignmentSubmissionStatusEnum | null;
  onUpdate?: (data: IAssignmentSubmissionRequest) => Promise<void>;
  isUpdating?: boolean;
  isSummative?: boolean;
}

export const Submission = ({
  student,
  submission,
  userRole,
  assignmentStatus,
  onUpdate,
  isUpdating = false,
  isSummative = false,
}: ISubmissionProps) => {
  const { text, submittedAt, files } = submission;
  const [isEditMode, setIsEditMode] = useState(false);
  const isTurnitinProcessing =
    submission.turnitin?.status === TurnitinSubmissionStatus.PROCESSING ||
    submission.turnitin?.similarityStatus === TurnitinSubmissionStatus.PROCESSING;
  const canEdit =
    userRole === 'student' &&
    assignmentStatus !== AssignmentSubmissionStatusEnum.Completed &&
    !isTurnitinProcessing;

  const handleUpdate = async (data: IAssignmentSubmissionRequest) => {
    if (onUpdate) {
      await onUpdate(data);
      setIsEditMode(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="font-medium">Submission</h3>
      <div className="flex flex-col rounded-[24px] gap-4 p-6 bg-white shadow-[0px_3px_12px_0px_#0000000D]">
        <div className="flex justify-between items-center gap-4 h-[36px]">
          <UserAvatar avatar={student.avatar} userName={student.username} />
          <div className="flex items-center gap-2">
            {submittedAt && (
              <p className="text-grey-secondary font-medium text-sm">
                Sent:
                <span className="ml-1 text-[#211D55] font-bold">
                  {formatDateTimeWithAMPM(submittedAt)}
                </span>
              </p>
            )}
            {canEdit && onUpdate && !isEditMode && (
              <div>
                <Tooltip content="Open edit mode" placement="top">
                  <Button
                    isIconOnly
                    variant="light"
                    onPress={() => setIsEditMode(true)}
                    className="h-8 w-8 min-w-8 p-2 rounded-full hover:bg-grey-50"
                    aria-label="Open edit mode"
                  >
                    <Svg Icon={EditIcon} className="size-5" />
                  </Button>
                </Tooltip>
              </div>
            )}
            {canEdit && isEditMode && (
              <div>
                <Tooltip content="Close edit mode" placement="top">
                  <Button
                    isIconOnly
                    variant="flat"
                    onPress={() => setIsEditMode(false)}
                    className="h-8 w-8 min-w-8 p-2 rounded-full bg-transparent text-red-500 hover:bg-red-100"
                    aria-label="Close edit mode"
                  >
                    <X className="h-5 min-w-5" />
                  </Button>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <p className="text-black">{text}</p>
        {files.length > 0 && <AttachmentsDownloadList attachments={files} />}
      </div>

      {isEditMode && onUpdate && (
        <MessageComposer
          withLayout
          draftKey={`submission:edit:${student.id}`}
          onSubmit={handleUpdate}
          isSubmitting={isUpdating}
          initialText={text}
          maxAttachments={isSummative ? 1 : 3}
        />
      )}
    </div>
  );
};
